import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useState, useEffect } from 'react';

// Material-UI breakpoints for reference
export const breakpoints = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536,
};

// Custom hook for responsive behavior
export const useResponsive = () => {
  const theme = useTheme();
  
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('xl'));

  return {
    isMobile,
    isTablet,
    isDesktop,
    isSmallScreen,
    isLargeScreen,
    breakpoint: isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop'
  };
};

// Hook for window dimensions
export const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Call handler right away so state gets updated with initial window size

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};

// Responsive styling helpers
export const responsiveStyles = {
  // Grid configurations
  gridConfig: {
    cards: {
      xs: 12,
      sm: 6,
      md: 4,
      lg: 3,
      xl: 2
    },
    marketplaceCards: {
      xs: 12,
      sm: 6,
      md: 4,
      lg: 3
    },
    profileCards: {
      xs: 12,
      sm: 6,
      md: 4
    }
  },

  // Container padding
  containerPadding: {
    xs: 1,
    sm: 2,
    md: 3,
    lg: 4
  },

  // Typography scales
  heroTitle: {
    fontSize: {
      xs: '2rem',
      sm: '2.5rem',
      md: '3rem',
      lg: '4rem',
      xl: '5rem'
    }
  },

  sectionTitle: {
    fontSize: {
      xs: '1.5rem',
      sm: '2rem',
      md: '2.5rem',
      lg: '3rem'
    }
  },

  // Card dimensions
  cardHeight: {
    mobile: 280,
    tablet: 320,
    desktop: 360
  },

  // Modal dimensions
  modalWidth: {
    xs: '95vw',
    sm: '80vw',
    md: '60vw',
    lg: '50vw',
    xl: '40vw'
  }
};

// Responsive component utilities
export const getResponsiveValue = (breakpoint, values) => {
  const orderedBreakpoints = ['xs', 'sm', 'md', 'lg', 'xl'];
  const breakpointIndex = orderedBreakpoints.indexOf(breakpoint);
  
  if (breakpointIndex === -1) return values.xs || values[Object.keys(values)[0]];
  
  // Find the closest defined value
  for (let i = breakpointIndex; i >= 0; i--) {
    const key = orderedBreakpoints[i];
    if (values[key] !== undefined) {
      return values[key];
    }
  }
  
  return values[Object.keys(values)[0]];
};

// Navigation helpers for mobile
export const mobileNavConfig = {
  drawerWidth: 280,
  appBarHeight: {
    mobile: 56,
    desktop: 64
  },
  bottomNavHeight: 56
};

// Responsive image configurations
export const imageConfigs = {
  avatar: {
    mobile: { width: 40, height: 40 },
    desktop: { width: 48, height: 48 }
  },
  profileAvatar: {
    mobile: { width: 80, height: 80 },
    desktop: { width: 120, height: 120 }
  },
  cardImage: {
    mobile: { aspectRatio: '2/3', maxHeight: 200 },
    tablet: { aspectRatio: '2/3', maxHeight: 250 },
    desktop: { aspectRatio: '2/3', maxHeight: 300 }
  }
};

// Form configurations
export const formConfigs = {
  maxWidth: {
    xs: '100%',
    sm: '500px',
    md: '600px',
    lg: '700px'
  },
  fieldSpacing: {
    mobile: 2,
    desktop: 3
  }
};

// Animation configurations for mobile
export const animationConfigs = {
  // Reduce motion for mobile performance
  transitions: {
    mobile: {
      duration: 200,
      easing: 'ease-out'
    },
    desktop: {
      duration: 300,
      easing: 'cubic-bezier(0.4, 0, 0.2, 1)'
    }
  },
  
  // Scroll configurations
  scrollBehavior: {
    mobile: 'auto', // Instant for better performance
    desktop: 'smooth'
  }
};

// Touch/click configurations
export const interactionConfigs = {
  // Minimum touch target size (44px recommended)
  minTouchTarget: 44,
  
  // Button sizes
  buttonSizes: {
    mobile: {
      small: { minHeight: 32, fontSize: '0.85rem' },
      medium: { minHeight: 40, fontSize: '0.9rem' },
      large: { minHeight: 48, fontSize: '1rem' }
    },
    desktop: {
      small: { minHeight: 28, fontSize: '0.85rem' },
      medium: { minHeight: 36, fontSize: '0.9rem' },
      large: { minHeight: 42, fontSize: '1rem' }
    }
  }
};

// Spacing helpers
export const spacingHelpers = {
  getSectionPadding: (breakpoint) => {
    switch (breakpoint) {
      case 'mobile': return { py: 3, px: 2 };
      case 'tablet': return { py: 4, px: 3 };
      default: return { py: 6, px: 4 };
    }
  },
  
  getCardPadding: (breakpoint) => {
    switch (breakpoint) {
      case 'mobile': return 1.5;
      case 'tablet': return 2;
      default: return 2.5;
    }
  },

  getGap: (breakpoint) => {
    switch (breakpoint) {
      case 'mobile': return 1;
      case 'tablet': return 2;
      default: return 3;
    }
  }
}; 